import { convertIElementToThreePositionParsed } from "@/utils/transform-conversion-parsed";
import { Img2dRenderer } from "@faro-lotv/app-component-toolbox";
import {
  IElement,
  IElementImg2d,
  IElementMarkupPolygon,
  IElementMeasurePolygon,
  isIElementImg2d,
  isIElementMarkupPolygon,
  isIElementMeasurePolygon,
  isIElementModel3d,
} from "@faro-lotv/ielement-types";
import { convertIElementToThreePosition } from "@faro-lotv/project-source";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { RefObject, forwardRef, useMemo, type JSX } from "react";
import { Group, Material, Mesh, Object3D } from "three";
import { MultiPointMeasureRenderer } from "../measurements/multi-point-measure-renderer";
import { ZipModel3dRenderer } from "../zip-model-3d-renderer";
import { AnnotationVisibility } from "./annotation-utils";

interface AnnotationObjectProps {
  /** The Annotation's element to render */
  annotation: IElement;

  /** Whether a collapsed variant of the annotation should be shown */
  isCollapsed?: boolean;

  /** Whether depth testing should be used to render the annotation */
  depthTest?: Material["depthTest"];
}

/**
 * @returns the visual representation of an annotation (without any behavior)
 */
export const AnnotationObject = forwardRef<Object3D, AnnotationObjectProps>(
  function AnnotationObject(
    { annotation, isCollapsed, depthTest }: AnnotationObjectProps,
    ref,
  ): JSX.Element | null {
    if (isIElementMarkupPolygon(annotation)) {
      return (
        <MarkupPolygonRenderer
          // The ref is also a valid Object3D, but TS doesn't support the automatic cast here
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          ref={ref as RefObject<Group | null>}
          annotation={annotation}
        />
      );
    } else if (isIElementImg2d(annotation)) {
      return (
        <SignedImg2dRenderer
          // The ref is also a valid Object3D, but TS doesn't support the automatic cast here
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          ref={ref as RefObject<Mesh | null>}
          annotation={annotation}
        />
      );
    } else if (isIElementModel3d(annotation)) {
      return (
        <ZipModel3dRenderer
          // The ref is also a valid Object3D, but TS doesn't support the automatic cast here
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          ref={ref as RefObject<Group | null>}
          iElement={annotation}
        />
      );
    } else if (isIElementMeasurePolygon(annotation)) {
      return (
        <MeasurePolygonAnnotationRenderer
          // The ref is also a valid Object3D, but TS doesn't support the automatic cast here
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          ref={ref as RefObject<Group | null>}
          measurement={annotation}
          isCollapsed={isCollapsed}
          depthTest={depthTest}
        />
      );
    }

    return null;
  },
);

type MarkupPolygonRendererProps = {
  /** IElement of the annotation */
  annotation: IElementMarkupPolygon;
};

/** @returns a point annotation object of type MarkupPolygon */
const MarkupPolygonRenderer = forwardRef<Group, MarkupPolygonRendererProps>(
  function MarkupPolygonRenderer(
    { annotation }: MarkupPolygonRendererProps,
    ref,
  ): JSX.Element {
    const markupPosition = useMemo(
      () => convertIElementToThreePosition(annotation.points[0]),
      [annotation.points],
    );

    return <group ref={ref} position={markupPosition} />;
  },
);

type MeasurePolygonAnnotationRendererProps = {
  /** The iElement containing the measurement*/
  measurement: IElementMeasurePolygon;

  /** Whether a collapsed variant of the annotation should be shown */
  isCollapsed?: boolean;

  /** Whether depth testing should be used to render the measurement */
  depthTest?: Material["depthTest"];
};

/** @returns A component to render measure polygon IElement*/
const MeasurePolygonAnnotationRenderer = forwardRef<
  Group,
  MeasurePolygonAnnotationRendererProps
>(function MeasurePolygonAnnotationRenderer(
  {
    measurement,
    isCollapsed,
    depthTest,
  }: MeasurePolygonAnnotationRendererProps,
  ref,
): JSX.Element | null {
  // Unfortunately, the geometry of the measurement is left handed.
  // Since the viewer cannot support it (all the world matrices are right handed)
  // we need to convert the geometry to be right-handed on import.
  const points = useMemo(
    () => measurement.points.map(convertIElementToThreePositionParsed),
    [measurement.points],
  );

  return (
    <MultiPointMeasureRenderer
      ref={ref}
      points={points}
      isClosed={measurement.isClosed}
      visibility={
        isCollapsed
          ? AnnotationVisibility.NotVisible
          : AnnotationVisibility.Visible
      }
      depthTest={depthTest}
      showActionBar={false}
      isApproximation={measurement.metaDataMap?.isEstimatedFrom2DPano}
    />
  );
});

type SignedImg2dRendererProps = {
  /** IElement of the annotation to render */
  annotation: IElementImg2d;
};

/** @returns A version of ACT's Img2dRenderer that uses signed URLs using the current ApiClientContext. */
const SignedImg2dRenderer = forwardRef<Mesh, SignedImg2dRendererProps>(
  function SignedImg2dRenderer({ annotation }, ref): JSX.Element {
    const { projectApiClient } = useApiClientContext();

    return (
      <Img2dRenderer
        ref={ref}
        iElement={annotation}
        projectApi={projectApiClient}
      />
    );
  },
);
